import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useUserSession } from '../../../context/UserSessionContext'
import { appPath } from '../../../utils/appPath'

type TabId =
  | 'access'
  | 'workspace'
  | 'features'
  | 'notifications'
  | 'audit'

const FY_MONTHS = [
  { v: 1, label: 'January' },
  { v: 2, label: 'February' },
  { v: 3, label: 'March' },
  { v: 4, label: 'April' },
  { v: 5, label: 'May' },
  { v: 6, label: 'June' },
  { v: 7, label: 'July' },
  { v: 8, label: 'August' },
  { v: 9, label: 'September' },
  { v: 10, label: 'October' },
  { v: 11, label: 'November' },
  { v: 12, label: 'December' },
] as const

function formatAuditAt(iso: string) {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

export const AdminSettingsPage: React.FC = () => {
  const {
    isAdmin,
    workspace,
    setWorkspace,
    features,
    setFeatures,
    notifications,
    setNotifications,
    auditLog,
    logAudit,
    clearAuditLog,
  } = useUserSession()

  const [tab, setTab] = useState<TabId>('access')
  const [savedFlash, setSavedFlash] = useState(false)

  const tabs = useMemo(
    () =>
      [
        { id: 'access' as const, label: 'Rates & access' },
        { id: 'workspace' as const, label: 'Org reporting' },
        { id: 'features' as const, label: 'Features' },
        { id: 'notifications' as const, label: 'Announcements' },
        { id: 'audit' as const, label: 'Audit log' },
      ] satisfies { id: TabId; label: string }[],
    []
  )

  const handleSave = (detail: string) => {
    logAudit('Admin settings saved', detail)
    setSavedFlash(true)
    window.setTimeout(() => setSavedFlash(false), 2200)
  }

  const clearAudit = () => {
    clearAuditLog()
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <PageHeader
          title="Admin settings"
          subtitle="Restricted to administrators."
        />
        <div className="p-6 max-w-lg">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950">
            <p className="font-semibold">Access denied</p>
            <p className="mt-1 text-amber-900/90">
              Your account does not have the admin role and is not listed in
              server <code className="text-xs bg-amber-100/80 px-1 rounded">admin-emails</code>.
              Ask an owner to grant access, or use{' '}
              <a href="/" className="underline font-medium text-amber-950">
                the classic app
              </a>{' '}
              for allowed-user management.
            </p>
          </div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <PageHeader
        title="Admin settings"
        subtitle="Organization defaults, feature flags, and local audit trail."
      />
      <div className="px-6 pt-3 pb-6">
        <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-3 mb-6">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`
                text-xs font-medium px-3 py-1.5 rounded-lg transition-colors
                ${tab === t.id
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-200/80'
                }
              `}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="max-w-3xl space-y-6">
          {tab === 'access' && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80">
                <h2 className="text-sm font-semibold text-slate-900">
                  Same tools as the classic sidebar
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Grade × tech rates and the allowed-email / role list use the
                  same APIs as v1.
                </p>
              </div>
              <div className="p-5 grid gap-3 sm:grid-cols-2">
                <Link
                  to={appPath('/rate-config')}
                  className="block rounded-lg border border-slate-200 p-4 hover:border-blue-300 hover:bg-slate-50/80 transition-colors"
                >
                  <p className="text-sm font-semibold text-slate-900">
                    Rate configure
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Technology × Grade matrix ($/hr). PATCH{' '}
                    <code className="text-[10px] bg-slate-100 px-1 rounded">
                      /api/grade-tech-permutations/bulk
                    </code>
                  </p>
                </Link>
                <Link
                  to={appPath('/email-access')}
                  className="block rounded-lg border border-slate-200 p-4 hover:border-blue-300 hover:bg-slate-50/80 transition-colors"
                >
                  <p className="text-sm font-semibold text-slate-900">
                    Email access & roles
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Whitelist sign-in emails and assign Finance / RMG / Admin.
                  </p>
                </Link>
              </div>
            </section>
          )}

          {tab === 'workspace' && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80">
                <h2 className="text-sm font-semibold text-slate-900">
                  Fiscal & date display
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Stored per browser; wire to shared config when API exists.
                </p>
              </div>
              <div className="p-5 space-y-4">
                <label className="block max-w-sm">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Fiscal year starts
                  </span>
                  <select
                    value={workspace.fiscalYearStartMonth}
                    onChange={e =>
                      setWorkspace({
                        fiscalYearStartMonth: Number(e.target.value),
                      })
                    }
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {FY_MONTHS.map(m => (
                      <option key={m.v} value={m.v}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block max-w-sm">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Date format
                  </span>
                  <select
                    value={workspace.dateFormat}
                    onChange={e =>
                      setWorkspace({
                        dateFormat: e.target.value as typeof workspace.dateFormat,
                      })
                    }
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="dmy">DD/MM/YYYY</option>
                    <option value="mdy">MM/DD/YYYY</option>
                    <option value="ymd">YYYY-MM-DD</option>
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => handleSave('Org reporting')}
                  className="rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700"
                >
                  Save section
                </button>
              </div>
            </section>
          )}

          {tab === 'features' && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80">
                <h2 className="text-sm font-semibold text-slate-900">
                  Feature flags
                </h2>
              </div>
              <div className="p-5 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-blue-600 rounded"
                    checked={features.showBetaNav}
                    onChange={e =>
                      setFeatures({ showBetaNav: e.target.checked })
                    }
                  />
                  <span className="text-sm text-slate-700">
                    Show beta navigation items (e.g. Subscriptions)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-blue-600 rounded"
                    checked={features.compactTables}
                    onChange={e =>
                      setFeatures({ compactTables: e.target.checked })
                    }
                  />
                  <span className="text-sm text-slate-700">
                    Compact tables (UI density — opt-in for future screens)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-blue-600 rounded"
                    checked={features.exportWatermark}
                    onChange={e =>
                      setFeatures({ exportWatermark: e.target.checked })
                    }
                  />
                  <span className="text-sm text-slate-700">
                    Watermark CSV exports with timestamp (when export uses v2
                    helper)
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => handleSave('Feature flags')}
                  className="mt-2 rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700"
                >
                  Save section
                </button>
              </div>
            </section>
          )}

          {tab === 'notifications' && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80">
                <h2 className="text-sm font-semibold text-slate-900">
                  Product announcements
                </h2>
              </div>
              <div className="p-5 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-blue-600 rounded"
                    checked={notifications.productUpdateEmails}
                    onChange={e =>
                      setNotifications({
                        productUpdateEmails: e.target.checked,
                      })
                    }
                  />
                  <span className="text-sm text-slate-700">
                    In-app / email product updates (preference only until
                    delivery is wired)
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => handleSave('Announcements')}
                  className="mt-2 rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700"
                >
                  Save section
                </button>
              </div>
            </section>
          )}

          {tab === 'audit' && (
            <section className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Local audit log
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Last {auditLog.length} events in this browser.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearAudit}
                  className="text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50"
                >
                  Clear log
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-2 font-semibold">When</th>
                      <th className="px-3 py-2 font-semibold">Action</th>
                      <th className="px-3 py-2 font-semibold">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-5 py-8 text-center text-slate-500"
                        >
                          No events yet. Saving profile or admin settings will
                          append here.
                        </td>
                      </tr>
                    ) : (
                      auditLog.map(row => (
                        <tr
                          key={row.id}
                          className="border-b border-slate-50 hover:bg-slate-50/80"
                        >
                          <td className="px-5 py-2 text-slate-600 whitespace-nowrap">
                            {formatAuditAt(row.at)}
                          </td>
                          <td className="px-3 py-2 text-slate-800">
                            {row.action}
                          </td>
                          <td className="px-3 py-2 text-slate-500">
                            {row.detail || '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {savedFlash && (
            <p className="text-sm text-emerald-600 font-medium">
              Saved — recorded in audit log.
            </p>
          )}
        </div>
      </div>
    </AppLayout>
  )
}

export default AdminSettingsPage
